import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  IconButton,
  CircularProgress,
  Stack,
  Pagination,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../components/common/ShowToast";
import { DataGrid, GridColDef, GridRowModel } from "@mui/x-data-grid";
import apiService from "../../../../../service/Api";
import { FileComment } from "../utils/processCSVFile";
import { ErrorComment } from "../utils/processExcelComment";
import CustomTextField from "../../../../../components/ui/Textfield";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import type { RootState } from "../../../../..//store/Index";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { tokens } from "../../../../../theme";

interface VerifyFileCommentsProps {
  fileComments: FileComment[];
  setFileComments: React.Dispatch<React.SetStateAction<FileComment[]>>;
  fetchComments: (initialFetch: boolean) => Promise<void>;
  commentsFileRef: React.RefObject<HTMLInputElement | null>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  errorComments: ErrorComment[];
  removeErrorRecord: (id: number) => void;
  changed: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
}

const FLAGGED_PAGE_SIZE = 5;

export const VerifyFileComments: React.FC<VerifyFileCommentsProps> = ({
  fileComments,
  setFileComments,
  fetchComments,
  commentsFileRef,
  errorComments,
  removeErrorRecord,
  changed,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<string>("correct");
  const [flaggedPage, setFlaggedPage] = useState<number>(1);
  const { id } = useParams<{ id: string }>();

  // Reset to page 1 when error comments change
  useEffect(() => {
    setFlaggedPage(1);
  }, [errorComments.length]);

  const paginatedErrorComments = errorComments.slice(
    (flaggedPage - 1) * FLAGGED_PAGE_SIZE,
    flaggedPage * FLAGGED_PAGE_SIZE,
  );

  const totalFlaggedPages = Math.ceil(errorComments.length / FLAGGED_PAGE_SIZE);

  const uploadComments = async () => {
    setIsUploading(true);

    const newComments = fileComments.map((comment) => {
      return {
        stream_id: Number(id),
        sender_full_name: `${comment.first_name} ${comment.last_name}`.trim(),
        sender_email: comment.email,
        content: comment.comment,
      };
    });

    try {
      await apiService.post<unknown>(
        "/api/chats/memorial-message/bulk-create/",
        { messages: newComments },
      );
      showSuccessToast("Comments uploaded");
      setFileComments([]);
      fetchComments(false);
    } catch (error: unknown) {
      console.error("Failed to upload comments:", error);
      showErrorToast("Failed to upload comments");
    } finally {
      setIsUploading(false);
    }
  };

  const openFileBrowseWindow = () => {
    commentsFileRef.current?.click();
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: "Item", width: 90 },
    {
      field: "first_name",
      headerName: "Author Name",
      width: 150,
      editable: true,
    },
    {
      field: "last_name",
      headerName: "Author Last Name",
      width: 150,
      editable: true,
    },
    {
      field: "email",
      headerName: "Author Email",
      width: 150,
      editable: true,
    },
    {
      field: "phone_number",
      headerName: "Author Phone Number",
      width: 150,
      editable: true,
    },
    {
      field: "comment",
      headerName: "Comment",
      flex: 1,
      editable: true,
    },
  ];

  const handleProcessRowUpdate = (newRow: GridRowModel) => {
    setFileComments((prevRows) =>
      prevRows.map((row) =>
        row.id === newRow.id ? { ...row, ...(newRow as FileComment) } : row,
      ),
    );
    return newRow;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <input
          ref={commentsFileRef}
          style={{ width: "100%", height: "100%", display: "none" }}
          type="file"
          accept=".xlsx, .xls, .csv, .json"
          onChange={(e) => changed(e)}
        />

        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          aria-label="basic tabs example"
        >
          <Tab
            value={"correct"}
            label={
              <Box display="flex" alignItems="center">
                <span>Comments</span>
                <Typography
                  variant="body2"
                  sx={{
                    borderRadius: "50%",
                    width: "25px",
                    height: "25px",
                    color: "#fff",
                    display: "grid",
                    placeContent: "center",
                    ml: 1,
                    backgroundColor: "#048b3dff",
                  }}
                >
                  {fileComments.length}
                </Typography>
              </Box>
            }
          />
          <Tab
            value={"error"}
            label={
              <Box display="flex" alignItems="center">
                <span>Flagged Comments</span>
                <Typography
                  variant="body2"
                  sx={{
                    borderRadius: "50%",
                    width: "25px",
                    height: "25px",
                    color: "#fff",
                    display: "grid",
                    placeContent: "center",
                    ml: 1,
                    backgroundColor: "#b31212ff",
                  }}
                >
                  {errorComments.length}
                </Typography>
              </Box>
            }
          />
        </Tabs>

        <Box>
          <Button
            variant="outlined"
            sx={{ mr: 2 }}
            onClick={openFileBrowseWindow}
            disabled={isUploading}
          >
            Choose File
          </Button>

          <Button
            variant="contained"
            sx={{ mr: 2 }}
            onClick={uploadComments}
            disabled={isUploading}
          >
            {isUploading ? <CircularProgress /> : "Upload"}
          </Button>
        </Box>
      </Box>

      {/* Tabs View */}

      {currentTab === "correct" && (
        <Box sx={{ height: "93%" }}>
          <DataGrid
            rows={fileComments}
            columns={columns}
            pageSizeOptions={[5]}
            disableRowSelectionOnClick
            processRowUpdate={handleProcessRowUpdate}
            hideFooterPagination
            hideFooter
            sx={{
              height: "100%",
              zIndex: 1,
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "transparent",
              },
              "& .MuiDataGrid-row": {
                backgroundColor: colors.primary[900],
              },
              cursor: "pointer",
            }}
          />
        </Box>
      )}

      {currentTab === "error" && (
        <Box sx={{ padding: 1 }}>
          {errorComments.length === 0 && (
            <Typography variant="body2" sx={{ textAlign: "center" }}>
              No comments to display at the moment
            </Typography>
          )}

          {errorComments.length > 0 && (
            <>
              {paginatedErrorComments.map((comment) => (
                <ErrorCommentBox
                  key={comment.id}
                  comment={comment}
                  fileComments={fileComments}
                  removeErrorRecord={removeErrorRecord}
                  setFileComments={setFileComments}
                />
              ))}

              <Box
                sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 1 }}
              >
                <Pagination
                  count={totalFlaggedPages}
                  page={flaggedPage}
                  onChange={(_, value) => setFlaggedPage(value)}
                  color="primary"
                  shape="rounded"
                />
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

interface ErrorCommentBoxProp {
  comment: ErrorComment;
  removeErrorRecord: (id: number) => void;
  fileComments: FileComment[];
  setFileComments: React.Dispatch<React.SetStateAction<FileComment[]>>;
}

const ErrorCommentBox: React.FC<ErrorCommentBoxProp> = ({
  comment,
  removeErrorRecord,
  fileComments,
  setFileComments,
}) => {
  const [correctedComment, setCorrectedComment] = useState<ErrorComment | null>(
    null,
  );
  const [isExpanded, setExpanded] = useState<boolean>(false);
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  useEffect(() => {
    setCorrectedComment(comment);
  }, [comment]);

  const handleCommentChange = (field: keyof ErrorComment, value: string) => {
    setCorrectedComment((prev) => {
      if (prev === null) {
        return null;
      }
      return { ...prev, [field]: value };
    });
  };

  const fixRecord = () => {
    if (!correctedComment?.first_name || !correctedComment?.last_name) {
      showErrorToast(
        "Cannot fix this record.Please include a first_name and last_name",
      );
      return;
    }

    if (!correctedComment.email || !correctedComment.email.includes("@")) {
      showErrorToast(
        "Cannot fix this record.Please include a valid email address",
      );
      return;
    }

    if (!correctedComment.comment) {
      showErrorToast("Cannot fix this record.Please include a comment");
      return;
    }

    const fixedRecord: FileComment = {
      id: fileComments.length + 1,
      first_name: correctedComment.first_name,
      last_name: correctedComment.last_name,
      email: correctedComment.email,
      phone_number: correctedComment.phone_number,
      comment: correctedComment.comment,
    };

    setFileComments([...fileComments, fixedRecord]);
    removeErrorRecord(comment.id);
    showSuccessToast("Record Corrected");
  };

  return (
    <Stack
      sx={{
        backgroundColor: colors.primary[800],
        borderRadius: ".5rem",
        width: "95%",
        marginInline: "auto",
        padding: "1.2rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: isExpanded ? "1px solid white" : undefined,
          mb: isExpanded ? 3 : undefined,
        }}
      >
        {!comment?.unknownFieldsArray ? (
          <Typography
            variant="body2"
            sx={{
              padding: "0.5rem",
              backgroundColor: "#b31212ff",
              color: "#fff",
              borderRadius: ".5rem",
              fontFamily: '"Mulish", sans-serif',
              fontWeight: "bold",
            }}
          >
            Missing fields
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{
              padding: "0.5rem",
              backgroundColor: "#b31212ff",
              color: "#fff",
              borderRadius: ".5rem",
              fontFamily: '"Mulish", sans-serif',
              fontWeight: "bold",
            }}
          >
            Unknown fields
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Button variant="text" sx={{ fontWeight: 600 }} onClick={fixRecord}>
            Mark as Fixed
          </Button>
          <IconButton
            sx={{
              mr: 1,
              display: "block",
              "&:hover": { color: "#a60b0bff" },
            }}
            onClick={() => removeErrorRecord(comment.id)}
          >
            <DeleteOutlineOutlinedIcon />
          </IconButton>

          <IconButton
            sx={{
              "&:hover": { color: colors.primary[400] },
            }}
            onClick={() => setExpanded(!isExpanded)}
          >
            {!isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>
      </Box>

      {isExpanded && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              gap: 5,
              marginBottom: "1.2rem",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                padding: "1rem",
                backgroundColor: colors.primary[900],
                borderRadius: "0.5rem",
                fontSize: ".9rem",
                width: "8rem",
              }}
            >
              first_name
            </Typography>

            <CustomTextField
              value={correctedComment?.first_name}
              onChange={(e) =>
                handleCommentChange("first_name", e.target.value)
              }
              fullWidth={true}
              variant="outlined"
              required
              size="medium"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              gap: 5,
              marginBottom: "1.2rem",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                padding: "1rem",
                backgroundColor: colors.primary[900],
                borderRadius: "0.5rem",
                fontSize: ".9rem",
                width: "8rem",
              }}
            >
              last_name
            </Typography>

            <CustomTextField
              value={correctedComment?.last_name}
              onChange={(e) => handleCommentChange("last_name", e.target.value)}
              fullWidth={true}
              label=""
              variant="outlined"
              required
              size="medium"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              gap: 5,
              marginBottom: "1.2rem",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                padding: "1rem",
                backgroundColor: colors.primary[900],
                borderRadius: "0.5rem",
                fontSize: ".9rem",
                width: "8rem",
              }}
            >
              email
            </Typography>

            <CustomTextField
              value={correctedComment?.email}
              onChange={(e) => handleCommentChange("email", e.target.value)}
              fullWidth={true}
              label=""
              variant="outlined"
              required
              size="medium"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              gap: 5,
              marginBottom: "1.2rem",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                padding: "1rem",
                backgroundColor: colors.primary[900],
                borderRadius: "0.5rem",
                fontSize: ".9rem",
                width: "8rem",
              }}
            >
              phone_number
            </Typography>

            <CustomTextField
              value={correctedComment?.phone_number}
              onChange={(e) =>
                handleCommentChange("phone_number", e.target.value)
              }
              fullWidth={true}
              label=""
              variant="outlined"
              required
              size="medium"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              gap: 5,
              marginBottom: "1.2rem",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                padding: "1rem",
                backgroundColor: colors.primary[900],
                borderRadius: "0.5rem",
                fontSize: ".9rem",
                width: "8rem",
              }}
            >
              comment
            </Typography>

            <CustomTextField
              value={correctedComment?.comment}
              onChange={(e) => handleCommentChange("comment", e.target.value)}
              fullWidth={true}
              label=""
              variant="outlined"
              required
              size="medium"
            />
          </Box>

          {comment.unknownFieldsArray !== undefined &&
            comment.unknownFieldsArray.map((field, index) => {
              return (
                <Box
                  key={`${field}-${index}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                    gap: 5,
                    marginBottom: "1.2rem",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      padding: "1rem",
                      backgroundColor: colors.primary[900],
                      borderRadius: "0.5rem",
                      fontSize: ".9rem",
                      width: "8rem",
                    }}
                  >
                    Unknown
                  </Typography>

                  <CustomTextField
                    value={field}
                    fullWidth={true}
                    label=""
                    variant="outlined"
                    size="medium"
                  />
                </Box>
              );
            })}
        </>
      )}
    </Stack>
  );
};

const fileCommentShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  first_name: PropTypes.string,
  last_name: PropTypes.string,
  authorName: PropTypes.string,
  authorEmail: PropTypes.string,
  email: PropTypes.string,
  phone_number: PropTypes.string,
  comment: PropTypes.string,
  content: PropTypes.string,
});

const errorCommentShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  first_name: PropTypes.string,
  last_name: PropTypes.string,
  email: PropTypes.string,
  phone_number: PropTypes.string,
  comment: PropTypes.string,
  unknownFieldsArray: PropTypes.arrayOf(PropTypes.string),
});

VerifyFileComments.propTypes = {
  fileComments: PropTypes.arrayOf(fileCommentShape).isRequired,
  setFileComments: PropTypes.func.isRequired,
  fetchComments: PropTypes.func.isRequired,
  commentsFileRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  setOpen: PropTypes.func.isRequired,
  errorComments: PropTypes.arrayOf(errorCommentShape).isRequired,
  removeErrorRecord: PropTypes.func.isRequired,
  changed: PropTypes.func.isRequired,
};

ErrorCommentBox.propTypes = {
  comment: errorCommentShape.isRequired,
  removeErrorRecord: PropTypes.func.isRequired,
  fileComments: PropTypes.arrayOf(fileCommentShape).isRequired,
  setFileComments: PropTypes.func.isRequired,
};
